import { useCallback } from 'react'

import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import {
  buildPaginatedEndpoint,
  convertDatesInObject,
  getNextPageParam,
} from '@/helpers/api'

import { organizationUsersQueryKeys } from '@/hooks/organization/types'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { User } from '@/types/User'

interface UseOrganizationUsersArgs<TResult> {
  orgId: UUID | ''
  select?: (
    users: InfiniteData<PaginatedResponse<User>, PaginateRequest>,
  ) => TResult
  enabled?: boolean
}

interface UseOrgUserArgs {
  orgId: UUID | ''
  userId: UUID | null
  enabled?: boolean
}

interface UseOrgUserByEmailArgs {
  orgId: UUID | ''
  email: string | null | undefined
}

const fetchOrganizationUsers = async (
  orgId: UUID | '',
  accessToken: string | null | undefined,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<User>> => {
  if (!orgId) {
    return { count: 0, has_more: false, data: [] }
  }
  if (!accessToken) {
    throw new Error('No access token')
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/organizations/${orgId}/users`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return convertDatesInObject(await res.json())
}

export const useOrganizationUsers = <TResult = User[]>({
  orgId,
  select,
  enabled = true,
}: UseOrganizationUsersArgs<TResult>) => {
  const { accessToken } = useAuthInfo()

  const selector = useCallback(
    (data: InfiniteData<PaginatedResponse<User>, PaginateRequest>) => {
      if (select) {
        return select(data)
      }
      return data.pages
        .map((res) => res.data)
        .flat()
        .sort((a, b) =>
          a.first_name.localeCompare(b.first_name),
        ) as unknown as TResult
    },
    [select],
  )

  return useInfiniteQuery({
    queryFn: ({ pageParam }) =>
      fetchOrganizationUsers(orgId, accessToken, pageParam),
    queryKey: organizationUsersQueryKeys.organization({ orgId }),
    initialPageParam: { limit: 10000 } as PaginateRequest,
    staleTime: 5 * 60 * 10000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: enabled && !!orgId && !!accessToken,
    getNextPageParam,
    select: selector,
  })
}

export const useOrganizationUser = ({
  orgId,
  userId,
  enabled,
}: UseOrgUserArgs) => {
  const selectUser = useCallback(
    (data: InfiniteData<PaginatedResponse<User>, PaginateRequest>) => {
      return data.pages
        .map((res) => res.data)
        .flat()
        .find((user) => user.id === userId)
    },
    [userId],
  )
  return useOrganizationUsers({ orgId, select: selectUser, enabled })
}

/**
 * The member of `orgId` with this email address, if any.
 *
 * For callers whose record of a person is an email rather than a user id —
 * document comment and tracked-change authors, for instance, which are written
 * into the file itself and carry no id. Matched case-insensitively, because the
 * address makes the round trip through storage formats that do not preserve
 * case. Resolves against the same cached roster `useOrganizationUser` reads, so
 * it costs no extra request.
 */
export const useOrganizationUserByEmail = ({
  orgId,
  email,
}: UseOrgUserByEmailArgs) => {
  const normalized = email?.trim().toLowerCase() || null
  const selectUser = useCallback(
    (data: InfiniteData<PaginatedResponse<User>, PaginateRequest>) => {
      if (!normalized) {
        return undefined
      }
      return data.pages
        .map((res) => res.data)
        .flat()
        .find((user) => user.email.toLowerCase() === normalized)
    },
    [normalized],
  )
  return useOrganizationUsers({ orgId, select: selectUser })
}
